import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTokens } from '../contexts/TokenContext';
import { Plus, Trash2, FileText, LogOut, Loader2 } from 'lucide-react';
import api from '../lib/api';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const { balance } = useTokens();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [createError, setCreateError] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const fetchRef = useRef(0);

  // Stable user id — avoids re-triggering on object reference changes
  const userId = user?.id || user?.email || null;

  const fetchProjects = useCallback(async () => {
    const fetchId = ++fetchRef.current;
    setLoading(true);
    try {
      const { data } = await api.get('/projects');
      // Only apply if this is still the latest fetch
      if (fetchRef.current === fetchId) {
        setProjects(data || []);
      }
    } catch (e) {
      console.error('Failed to load projects:', e);
      // On auth error, redirect; otherwise keep whatever projects we had
    } finally {
      if (fetchRef.current === fetchId) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!userId) { setLoading(false); return; }
    fetchProjects();
  }, [userId, authLoading, fetchProjects]);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    setCreateError('');
    try {
      const { data } = await api.post('/projects', { title: newTitle.trim(), format: 'feature_film', target_pages: 100 });
      if (!data?.id) { setCreateError('Something went wrong. Please try again.'); setCreating(false); return; }
      api.put(`/projects/${data.id}/fields`, { title: newTitle.trim() }).catch(() => {});
      navigate(`/project/${data.id}`);
    } catch (error) {
      if (error.response?.status === 401) navigate('/login');
      else setCreateError(error.response?.data?.detail || 'Could not create project. Please try again.');
      setCreating(false);
    }
  };

  const handleDeleteClick = (e, id) => {
    e.stopPropagation();
    e.preventDefault();
    setConfirmDeleteId(id);
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDeleteId || deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/projects/${confirmDeleteId}`);
      setProjects(prev => prev.filter(p => p.id !== confirmDeleteId));
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setConfirmDeleteId(null);
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen relative" style={{ fontFamily: "'Manrope', sans-serif", background: 'linear-gradient(180deg, #0B0F1A 0%, #0E1320 100%)' }}>
      {/* Film grain */}
      <div aria-hidden="true" className="fixed inset-0 z-50 pointer-events-none opacity-[0.03] mix-blend-overlay" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`, backgroundRepeat: 'repeat', backgroundSize: '150px' }} />

      <header className="sticky top-0 z-40 backdrop-blur-xl border-b border-[#F5EDE4]/[0.06]" style={{ backgroundColor: 'rgba(11,15,26,0.7)' }}>
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="text-base font-medium tracking-tight text-[#F5EDE4]" data-testid="dashboard-brand">Scriptwriter Pro</span>
          <div className="flex items-center gap-5">
            <button onClick={() => navigate('/pricing')} className="text-xs text-[#F5EDE4]/40 hover:text-[#D6A85C] transition-colors duration-300" data-testid="nav-pricing">Pricing</button>
            {balance !== null && (
              <button onClick={() => navigate('/pricing')} className="text-xs text-[#F5EDE4]/40 hover:text-[#D6A85C] transition-colors" style={{ fontFamily: "'JetBrains Mono', monospace" }} data-testid="token-balance">{balance} credits</button>
            )}
            <span className="text-xs text-[#F5EDE4]/40">{user?.name || user?.email}</span>
            <button onClick={logout} className="text-[#F5EDE4]/40 hover:text-[#D6A85C] transition-colors duration-300" data-testid="logout-btn"><LogOut className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16 relative z-10">
        <div className="flex items-center justify-between mb-14">
          <h2 className="text-3xl font-medium tracking-tight text-[#F5EDE4]" style={{ fontFamily: "'Playfair Display', serif" }}>Your Films</h2>
          <button onClick={() => setShowNew(true)} className="flex items-center gap-2 px-5 py-2.5 text-xs font-medium active:scale-[0.98] transition-all duration-300" style={{ backgroundColor: '#D6A85C', color: '#0B0F1A' }} data-testid="new-project-btn">
            <Plus className="h-3.5 w-3.5" /> New Film
          </button>
        </div>

        {showNew && (
          <div className="mb-10 p-8 border border-[#F5EDE4]/[0.06]" style={{ backgroundColor: 'rgba(245,237,228,0.02)' }} data-testid="new-project-form">
            {createError && <p className="text-red-400/80 text-xs mb-4" data-testid="create-error">{createError}</p>}
            <input autoFocus type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreate()}
              placeholder="What's your film called?"
              className="w-full bg-transparent border-b border-[#F5EDE4]/[0.08] focus:border-[#D6A85C]/40 outline-none py-2 text-[#F5EDE4] placeholder:text-[#F5EDE4]/25 text-base mb-5"
              data-testid="new-project-title"
            />
            <div className="flex gap-3">
              <button onClick={handleCreate} disabled={!newTitle.trim() || creating} className="px-5 py-2 text-xs font-medium disabled:opacity-20 transition-all duration-300" style={{ backgroundColor: '#D6A85C', color: '#0B0F1A' }} data-testid="create-project-btn">{creating ? 'Creating...' : 'Create'}</button>
              <button onClick={() => { setShowNew(false); setNewTitle(''); }} className="px-5 py-2 text-xs text-[#F5EDE4]/40 hover:text-[#F5EDE4] transition-colors duration-300">Cancel</button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-5 w-5 animate-spin text-[#D6A85C]/50" /></div>
        ) : projects.length === 0 && !showNew ? (
          <div className="text-center py-24">
            <p className="text-[#F5EDE4]/40 text-sm mb-2">No films yet.</p>
            <p className="text-[#F5EDE4]/25 text-xs mb-8 italic" style={{ fontFamily: "'Playfair Display', serif" }}>Every great film starts with a blank page.</p>
            <button onClick={() => setShowNew(true)} className="px-8 py-3 text-xs font-medium active:scale-[0.98] transition-all duration-300" style={{ backgroundColor: '#D6A85C', color: '#0B0F1A' }}>Start Your First Film</button>
          </div>
        ) : (
          <div className="border-t border-[#F5EDE4]/[0.06]">
            {projects.map(project => (
              <div key={project.id} onClick={() => navigate(`/project/${project.id}`)} className="group flex items-center justify-between py-5 px-4 border-b border-[#F5EDE4]/[0.06] cursor-pointer hover:bg-[#F5EDE4]/[0.02] transition-all duration-300" data-testid={`project-card-${project.id}`}>
                <div className="flex items-center gap-4">
                  <FileText className="h-4 w-4 text-[#D6A85C]/30" />
                  <div>
                    <p className="text-sm text-[#F5EDE4] group-hover:text-[#D6A85C] transition-colors">{project.title}</p>
                    <p className="text-xs text-[#F5EDE4]/25 mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{project.status === 'screenplay' ? 'Draft ready' : 'New project'}</p>
                  </div>
                </div>
                <button onClick={e => handleDeleteClick(e, project.id)} className="text-[#F5EDE4]/15 hover:text-red-400 transition-all duration-300 p-2" data-testid={`delete-project-${project.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Delete confirmation */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center" style={{ backgroundColor: 'rgba(11,15,26,0.85)' }} data-testid="delete-confirm-modal">
          <div className="w-full max-w-sm mx-4 p-6 border border-[#F5EDE4]/[0.06]" style={{ backgroundColor: '#0E1320', fontFamily: "'Manrope', sans-serif" }}>
            <p className="text-sm text-[#F5EDE4] mb-1 font-medium">Delete this project?</p>
            <p className="text-xs text-[#F5EDE4]/35 mb-6">This cannot be undone. Any screenplay text will be permanently removed.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDeleteId(null)} className="px-4 py-2 text-xs text-[#F5EDE4]/40 border border-[#F5EDE4]/[0.06] hover:text-[#F5EDE4]/60 transition-colors" data-testid="delete-cancel">Cancel</button>
              <button onClick={handleDeleteConfirm} disabled={deleting} className="px-4 py-2 text-xs text-red-400 border border-red-400/20 hover:bg-red-400/10 disabled:opacity-50 transition-colors" data-testid="delete-confirm">
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
